import { useState, useRef, useEffect } from "react";
import {
  Phone,
  Menu,
  X,
  ChevronDown,
  Wrench,
  Shield,
  Hammer,
  Sun,
  Droplets,
  Eye,
  Layers,
  Palette,
  ArrowRight,
  Calculator,
} from "lucide-react";
import { Link, useLocation } from "react-router-dom";
import { Button } from "@/components/ui/button";
import zenithLogo from "@/assets/zenith-logo.webp";

const navLinks = [
  { label: "Home", href: "/" },
  { label: "Why Zenith", href: "/#why-zenith" },
  { label: "Testimonials", href: "/#testimonials" },
  { label: "FAQ", href: "/#faq" },
];

const serviceLinks = [
  { icon: Shield, label: "Roofing", href: "/services/roofing", desc: "Replacements, repairs & inspections" },
  { icon: Hammer, label: "General Construction", href: "/services/construction", desc: "Remodels, additions & more" },
  { icon: Sun, label: "Solar", href: "/services/solar", desc: "Custom solar panel systems" },
  {
    icon: Droplets,
    label: "Mold & Water Mitigation",
    href: "/services/mold-remediation",
    desc: "Emergency remediation",
  },
];

const toolLinks = [
  {
    icon: Eye,
    label: "Design EyeQ® Visualizer",
    href: "/tools/design-eyeq",
    desc: "Try shingle colors on your home",
    featured: true,
  },
  {
    icon: Sun,
    label: "Solar Savings Calculator",
    href: "/tools/solar-calculator",
    desc: "Estimate your solar savings",
  },
  { icon: Layers, label: "Shingle Explorer", href: "/tools/shingle-explorer", desc: "Browse shingle products" },
  {
    icon: Shield,
    label: "Total Protection System®",
    href: "/tools/total-protection",
    desc: "Integrated roofing system",
  },
  { icon: Palette, label: "Design & Inspire", href: "/tools/design-inspire", desc: "Curated style boards" },
  {
    icon: Calculator,
    label: "Estimate Calculator",
    href: "/tools/estimate-calculator",
    desc: "Instant project pricing",
  },
];

const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [servicesOpen, setServicesOpen] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const [mobileServicesOpen, setMobileServicesOpen] = useState(false);
  const [mobileToolsOpen, setMobileToolsOpen] = useState(false);
  const servicesRef = useRef<HTMLDivElement>(null);
  const toolsRef = useRef<HTMLDivElement>(null);
  const location = useLocation();

  useEffect(() => {
    setOpen(false);
    setServicesOpen(false);
    setToolsOpen(false);
  }, [location]);

  useEffect(() => {
    const handleClick = (e: MouseEvent) => {
      if (servicesRef.current && !servicesRef.current.contains(e.target as Node)) setServicesOpen(false);
      if (toolsRef.current && !toolsRef.current.contains(e.target as Node)) setToolsOpen(false);
    };
    document.addEventListener("mousedown", handleClick);
    return () => document.removeEventListener("mousedown", handleClick);
  }, []);

  const handleNavClick = (href: string) => {
    setOpen(false);
    if (href.includes("#") && location.pathname === "/") {
      const id = href.split("#")[1];
      document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <nav className="fixed top-0 left-0 right-0 z-50 bg-background/95 backdrop-blur-md border-b">
      <div className="container mx-auto flex items-center justify-between h-20 px-4">
        <Link to="/" className="flex items-center gap-2">
          <img src={zenithLogo} alt="Zenith Solutions" className="h-12" width={141} height={48} />
        </Link>

        {/* Desktop */}
        <div className="hidden lg:flex items-center gap-5 absolute left-1/2 -translate-x-1/2">
          {navLinks.slice(0, 1).map((l) => (
            <Link
              key={l.href}
              to={l.href}
              onClick={() => handleNavClick(l.href)}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              {l.label}
            </Link>
          ))}

          {/* Services Mega Menu */}
          <div ref={servicesRef} className="relative">
            <button
              onClick={() => {
                setServicesOpen(!servicesOpen);
                setToolsOpen(false);
              }}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
            >
              Services{" "}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${servicesOpen ? "rotate-180" : ""}`} />
            </button>
            {servicesOpen && (
              <div className="absolute top-full mt-3 left-1/2 -translate-x-1/2 w-[480px] bg-background border rounded-2xl shadow-2xl z-50 p-6 animate-fade-in">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-4">Our Services</p>
                <div className="grid grid-cols-2 gap-3">
                  {serviceLinks.map((s) => (
                    <Link
                      key={s.href}
                      to={s.href}
                      className="flex items-start gap-3 p-3 rounded-xl hover:bg-secondary transition-colors group"
                    >
                      <div className="w-10 h-10 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0 group-hover:bg-primary group-hover:text-primary-foreground transition-colors">
                        <s.icon className="h-5 w-5 text-primary group-hover:text-primary-foreground" />
                      </div>
                      <div>
                        <p className="text-sm font-bold text-foreground group-hover:text-primary transition-colors">
                          {s.label}
                        </p>
                        <p className="text-xs text-muted-foreground">{s.desc}</p>
                      </div>
                    </Link>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t">
                  <Link
                    to="/#services"
                    onClick={() => handleNavClick("/#services")}
                    className="text-sm font-bold text-primary hover:text-primary/80 inline-flex items-center gap-1"
                  >
                    View All Services <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            )}
          </div>

          {navLinks.slice(1).map((l) => (
            <Link
              key={l.href}
              to={l.href}
              onClick={() => handleNavClick(l.href)}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors"
            >
              {l.label}
            </Link>
          ))}

          {/* Tools dropdown */}
          <div ref={toolsRef} className="relative">
            <button
              onClick={() => {
                setToolsOpen(!toolsOpen);
                setServicesOpen(false);
              }}
              className="text-sm font-medium text-muted-foreground hover:text-primary transition-colors inline-flex items-center gap-1"
            >
              <Wrench className="h-3.5 w-3.5" /> Tools{" "}
              <ChevronDown className={`h-3.5 w-3.5 transition-transform ${toolsOpen ? "rotate-180" : ""}`} />
            </button>
            {toolsOpen && (
              <div className="absolute top-full mt-3 right-0 w-[320px] bg-background border rounded-2xl shadow-2xl z-50 p-4 animate-fade-in">
                <p className="text-xs font-bold uppercase tracking-widest text-primary mb-3 px-2">Homeowner Tools</p>
                {toolLinks.map((t) => (
                  <Link
                    key={t.href}
                    to={t.href}
                    className={`flex items-start gap-3 px-3 py-2.5 rounded-xl hover:bg-secondary transition-colors group ${t.featured ? "bg-primary/5 border border-primary/20 mb-2" : ""}`}
                  >
                    <div
                      className={`w-8 h-8 rounded-lg flex items-center justify-center flex-shrink-0 ${t.featured ? "bg-primary text-primary-foreground" : "bg-primary/10"}`}
                    >
                      <t.icon className={`h-4 w-4 ${t.featured ? "text-primary-foreground" : "text-primary"}`} />
                    </div>
                    <div>
                      <p className="text-sm font-bold text-foreground">{t.label}</p>
                      <p className="text-xs text-muted-foreground">{t.desc}</p>
                    </div>
                    {t.featured && (
                      <span className="text-[10px] font-bold uppercase tracking-wider text-primary bg-primary/10 rounded-full px-2 py-0.5 ml-auto self-center">
                        Popular
                      </span>
                    )}
                  </Link>
                ))}
                <div className="mt-3 pt-3 border-t px-2">
                  <Link
                    to="/tools"
                    className="text-sm font-bold text-primary hover:text-primary/80 inline-flex items-center gap-1"
                  >
                    All Tools <ArrowRight className="h-3.5 w-3.5" />
                  </Link>
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Navbar CTA — office number for general inquiries */}
        <a href="tel:5056048536" className="hidden lg:block">
          <Button size="sm" className="bg-primary text-primary-foreground hover:bg-primary/90 font-bold gap-1.5">
            <Phone className="h-3.5 w-3.5" /> (505) 604-8536
          </Button>
        </a>

        {/* Mobile toggle */}
        <button className="lg:hidden p-2" onClick={() => setOpen(!open)} aria-label="Menu">
          {open ? <X className="h-5 w-5" /> : <Menu className="h-5 w-5" />}
        </button>
      </div>

      {/* Mobile menu */}
      {open && (
        <div className="lg:hidden bg-background border-b px-4 pb-4 space-y-1 max-h-[80vh] overflow-y-auto">
          <Link
            to="/"
            onClick={() => setOpen(false)}
            className="block py-3 text-sm font-medium text-foreground hover:text-primary border-b border-border/50"
          >
            Home
          </Link>

          {/* Mobile Services */}
          <button
            onClick={() => setMobileServicesOpen(!mobileServicesOpen)}
            className="flex items-center justify-between w-full py-3 text-sm font-medium text-foreground hover:text-primary border-b border-border/50"
          >
            Services{" "}
            <ChevronDown className={`h-4 w-4 transition-transform ${mobileServicesOpen ? "rotate-180" : ""}`} />
          </button>
          {mobileServicesOpen && (
            <div className="pl-2 space-y-1 pb-2">
              {serviceLinks.map((s) => (
                <Link
                  key={s.href}
                  to={s.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-secondary transition-colors"
                >
                  <s.icon className="h-4 w-4 text-primary" />
                  <div>
                    <p className="text-sm font-medium text-foreground">{s.label}</p>
                    <p className="text-xs text-muted-foreground">{s.desc}</p>
                  </div>
                </Link>
              ))}
            </div>
          )}

          {navLinks.slice(1).map((l) => (
            <Link
              key={l.href}
              to={l.href}
              onClick={() => {
                handleNavClick(l.href);
                setOpen(false);
              }}
              className="block py-3 text-sm font-medium text-foreground hover:text-primary border-b border-border/50"
            >
              {l.label}
            </Link>
          ))}

          {/* Mobile Tools */}
          <button
            onClick={() => setMobileToolsOpen(!mobileToolsOpen)}
            className="flex items-center justify-between w-full py-3 text-sm font-medium text-foreground hover:text-primary border-b border-border/50"
          >
            <span className="flex items-center gap-2">
              <Wrench className="h-4 w-4" /> Tools
            </span>
            <ChevronDown className={`h-4 w-4 transition-transform ${mobileToolsOpen ? "rotate-180" : ""}`} />
          </button>
          {mobileToolsOpen && (
            <div className="pl-2 space-y-1 pb-2">
              {toolLinks.map((t) => (
                <Link
                  key={t.href}
                  to={t.href}
                  onClick={() => setOpen(false)}
                  className="flex items-center gap-3 py-2.5 px-3 rounded-lg hover:bg-secondary transition-colors"
                >
                  <t.icon className="h-4 w-4 text-primary" />
                  <span className="text-sm text-foreground">{t.label}</span>
                  {t.featured && (
                    <span className="text-[10px] font-bold text-primary bg-primary/10 rounded-full px-2 py-0.5 ml-auto">
                      Popular
                    </span>
                  )}
                </Link>
              ))}
              <Link
                to="/tools"
                onClick={() => setOpen(false)}
                className="block py-2 px-3 text-sm font-bold text-primary"
              >
                View All Tools →
              </Link>
            </div>
          )}

          <a href="tel:5056048536" className="block pt-2">
            <Button className="w-full bg-primary text-primary-foreground hover:bg-primary/90 font-bold gap-1.5">
              <Phone className="h-4 w-4" /> (505) 264-0170
            </Button>
          </a>
        </div>
      )}
    </nav>
  );
};

export default Navbar;
